/* eslint-disable react/display-name */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useEffect, useState } from 'react';
// import { dequal } from 'dequal';
import { useTranslations } from 'next-intl';
import { useContextualRouting } from 'next-use-contextual-routing';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useSWRInfinite from 'swr/infinite';
import { CountdownSimple } from '@components/countdown-simple';
import { Creators } from '@components/creators';
import { Box, Button, Container, Step, Text } from '@components/design-system';
import { ContextualTokenModal } from '@containers/contextual-token-modal';
import { IToken } from '@interface/token';
import { tokensFetcher } from '@utils/fetcher';
import { getCurrencyJSX } from '@utils/market';
import { useMedia } from '@utils/use-media';
import * as Styles from './styles';

const MediaWrapper = dynamic(() => import('@components/media'), { ssr: false });
const Grid = dynamic(() => import('@components/grid'), { ssr: false });

interface ISmartGridToken {
  api: string;
  query?: any;
  limit: number;
  masonry: boolean;
  info: boolean;
  market: boolean;
  labels: boolean;
  nodata?: string;
  maxtime?: number;
  sorting?: string;
}

export const SmartGridToken: FC<ISmartGridToken> = ({
  api,
  query,
  limit,
  masonry,
  info,
  market,
  labels,
  nodata = 'No data to display',
  maxtime,
  sorting,
}) => {
  const t = useTranslations('common');
  const { makeContextualHref, returnHref } = useContextualRouting();
  const [index, setIndex] = useState(-1);
  const router = useRouter();
  const columns = useMedia(['(min-width: 1440px)', '(min-width: 640px)'], [5, 3], 2);
  const space = useMedia(['(min-width: 1440px)', '(min-width: 640px)'], ['32px', '16px'], '0px');

  const getKey = (pageIndex: number, previousPageData: any) => {
    if (previousPageData && !previousPageData.length) return null; // reached the end

    // merge all query parameters
    const queryParams = {
      ...query,
      ...(sorting && { sorting }),
      limit,
      offset: (pageIndex + 0) * limit,
      ...(maxtime && { maxtime }),
    };

    return `${api}?${new URLSearchParams(queryParams).toString()}`;
  };

  const { data, error, size, setSize, isValidating } = useSWRInfinite(getKey, tokensFetcher, {
    revalidateIfStale: false,
    revalidateOnMount: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    refreshInterval: 0,
    persistSize: true,
    // compare: (a, b) => {
    //   const aMap = a && a[0].map((e: any) => e.id);
    //   const bMap = b && b[0].map((e: any) => e.id);
    //   const equal = dequal(aMap, bMap);
    //   return equal;
    // },
  });

  const [items, setItems] = useState([]);
  useEffect(() => {
    // don't update items if we are in contextual item modal and we already have items loaded
    if (router.query.tokenId && items.length !== 0) return;

    setItems(data ? [].concat(...data) : []);
  }, [data]);

  const isLoadingInitialData = !data && !error;
  const isLoadingMore = isLoadingInitialData || (size > 0 && data && typeof data[size - 1] === 'undefined');
  const isEmpty = data && data.length === 0;
  const isReachingEnd = isEmpty || (data && data[data.length - 1]?.length < limit);
  const isRefreshing = isValidating && data && data.length === size;

  const modalChange = (increment: number) => {
    // only allow keyboard navigation if its coming from a smart-grid-token component
    if (router.query.tokenId) {
      const next = index + increment;
      if (next >= 0 && next < items.length) {
        const token: IToken = items[next];

        router.push(makeContextualHref({ tokenId: token.id }), `/token/${token.contract.name}/${token.token_id}`, {
          scroll: false,
          shallow: true,
          locale: router.locale,
        });
      }
    }
  };

  const modalClose = () => {
    router.push(returnHref, undefined, { scroll: false, shallow: true, locale: router.locale });
  };

  const renderThumbnail = (token: IToken) => {
    const hasBidder =
      token.prio_auction && token.prio_auction.length > 0 ? token.prio_auction[0]?.current_bidder : false;

    return (
      <div key={token.id} style={{ overflow: 'hidden' }}>
        <Link
          scroll={false}
          shallow
          href={makeContextualHref({ tokenId: token.id })}
          as={`/token/${token.contract.name}/${token.token_id}`}
          locale={router.locale}
        >
          <a>
            <MediaWrapper token={token} inDetailView={false} labels={labels} />
          </a>
        </Link>

        {(info || market) && (
          <Box
            css={{
              flexDirection: 'column',

              ...(market && {
                borderBottomLeftRadius: 5,
                borderBottomRightRadius: 5,
                backgroundColor: '$secondaryBackground',
              }),
            }}
          >
            {info && (
              <Box css={{ justifyContent: 'space-between', margin: '$space$4' }}>
                <Box css={{ flexDirection: 'column', width: '100%' }}>
                  <Text unset ellipsis css={{ fontWeight: 700, overflow: 'hidden' }}>
                    {token.title}
                  </Text>
                  <Creators creators={token.creators} />
                </Box>
              </Box>
            )}

            {info && market && (
              <Box
                css={{
                  borderTop: '1px solid $colors$primaryBorder',
                }}
              />
            )}

            {market && token.prio_auction && token.prio_auction.length === 0 && (
              <Box
                css={{
                  flexDirection: 'column',
                  margin: '$space$4',
                }}
              >
                <Box css={{ justifyContent: 'space-between' }} vertical>
                  <Text
                    unset
                    ellipsis
                    css={{
                      color: '$secondaryFill',
                      overflow: 'hidden',
                      fontSize: 14,
                    }}
                  >
                    Supply
                  </Text>

                  <Text
                    unset
                    ellipsis
                    css={{
                      color: '$secondaryFill',
                      overflow: 'hidden',
                      fontSize: 14,
                    }}
                  >
                    Price
                  </Text>
                </Box>

                <Box css={{ justifyContent: 'space-between' }} vertical>
                  <Text
                    unset
                    css={{
                      color: '$primaryFill',
                      fontSize: 14,
                    }}
                  >
                    x {token.supply}
                  </Text>

                  <Text
                    unset
                    ellipsis
                    css={{
                      color: '$primaryFill',
                      overflow: 'hidden',
                      fontSize: 14,
                    }}
                  >
                    {token.prio_swap && token.prio_swap.length > 0
                      ? getCurrencyJSX(token.prio_swap[0]?.current_price || 0, token.prio_swap[0]?.currency)
                      : '-'}
                  </Text>
                </Box>
              </Box>
            )}
            {market && token.prio_auction && token.prio_auction.length > 0 && (
              <Box
                css={{
                  flexDirection: 'column',
                  margin: '$space$4',
                }}
              >
                <Box css={{ justifyContent: 'space-between' }} vertical>
                  <Text
                    unset
                    ellipsis
                    css={{
                      color: '$secondaryFill',
                      overflow: 'hidden',
                      fontSize: 14,
                    }}
                  >
                    {hasBidder ? 'Current bid' : 'Reserve'}
                  </Text>

                  <Text
                    unset
                    ellipsis
                    css={{
                      color: '$secondaryFill',
                      overflow: 'hidden',
                      fontSize: 14,
                    }}
                  >
                    Ending in
                  </Text>
                </Box>

                <Box css={{ justifyContent: 'space-between' }} vertical>
                  <Text
                    unset
                    css={{
                      color: '$primaryFill',
                      fontSize: 14,
                    }}
                  >
                    {getCurrencyJSX(token.prio_auction[0]?.current_bid, token.prio_auction[0].currency)}
                  </Text>

                  <CountdownSimple auctionEnd={new Date(token.prio_auction[0]?.end_time).getTime()} />
                </Box>
              </Box>
            )}
          </Box>
        )}
      </div>
    );
  };

  useEffect(() => {
    const nextIndex = items.findIndex((e: IToken) => String(e.id) === router.query.tokenId);
    if (nextIndex !== index) {
      setIndex(nextIndex);
    }
  }, [items, router.query.tokenId]);

  return (
    <div>
      {!isLoadingInitialData && items.length === 0 && (
        <Container>
          <Text unset css={{ color: '$secondaryFill' }}>
            {nodata}
          </Text>
        </Container>
      )}

      {masonry ? (
        <Grid columns={columns} space={space}>
          {items.map((token: IToken) => renderThumbnail(token))}
        </Grid>
      ) : (
        <Styles.VerticalContainer>{items.map((token: IToken) => renderThumbnail(token))}</Styles.VerticalContainer>
      )}

      {!isReachingEnd && (
        <Box css={{ padding: '$1 0' }} horizontal>
          {isLoadingMore ? (
            <Step progress />
          ) : (
            <Button onClick={() => setSize(size + 1)} variant="secondary" disabled={isRefreshing}>
              {t('load-more')}
            </Button>
          )}
        </Box>
      )}

      <ContextualTokenModal
        visible={!!router.query.tokenId}
        token={items[index]}
        onChange={modalChange}
        onClose={modalClose}
      />
    </div>
  );
};
